import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import SystemDataService from '../../Services/SystemDataService';
import { getLandingData } from '../../Public/landing-data';
import MediaComponent from '../Media/media-component';

const DRAG_THRESHOLD = 40;

const SpecialsBannerSection = ({
    sectionData,
    images: propImages,
    mobileImages: propMobileImages,
}) => {
    const { dir, language } = useSelector((state) => state.systemData || {});
    const defaultStrings = getLandingData(language).specialsBannerSection || {};
    const { images: dataImages, mobileImages: dataMobileImages } = sectionData || defaultStrings;

    const [images, setImages] = useState([]);
    const [mobileImages, setMobileImages] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const dragStartX = useRef(null);

    // ── [1] Load images ──────────────────────────────────────────────
    useEffect(() => {
        const extractUrl = (img) => (typeof img === 'string' ? img : img.image || img.url || '');
        const source = propImages || dataImages;
        const mobileSource = propMobileImages || dataMobileImages;
        if (source?.length) {
            setImages(source.map(extractUrl).filter(Boolean));
        } else {
            SystemDataService.getSpecialsImages().then((data) => {
                if (data?.length) setImages(data.map(extractUrl).filter(Boolean));
            });
        }
        if (mobileSource?.length) {
            setMobileImages(mobileSource.map(extractUrl).filter(Boolean));
        }
    }, [propImages, dataImages, propMobileImages, dataMobileImages]);

    // ── [2] Auto-cycle every 4s ──────────────────────────────────────
    useEffect(() => {
        if (images.length < 2) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [images.length]);

    const goTo = useCallback((i) => setCurrentIndex(i), []);
    const prev = useCallback(() => setCurrentIndex((p) => (p - 1 + images.length) % images.length), [images.length]);
    const next = useCallback(() => setCurrentIndex((p) => (p + 1) % images.length), [images.length]);

    // ── [3] Drag / swipe handlers ────────────────────────────────────
    const onMouseDown = (e) => { dragStartX.current = e.clientX; };
    const onMouseUp = (e) => {
        if (dragStartX.current === null) return;
        const delta = e.clientX - dragStartX.current;
        if (Math.abs(delta) > DRAG_THRESHOLD) delta < 0 ? next() : prev();
        dragStartX.current = null;
    };
    const onTouchStart = (e) => { dragStartX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (dragStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - dragStartX.current;
        if (Math.abs(delta) > DRAG_THRESHOLD) delta < 0 ? next() : prev();
        dragStartX.current = null;
    };

    if (!images.length) return null;

    return (
        <div dir={dir} className="w-full py-2 px-4 md:px-6 bg-white">
            <div className="max-w-[1400px] mx-auto">

                <div
                    className="relative w-full overflow-hidden rounded-lg cursor-grab active:cursor-grabbing select-none"
                    onMouseDown={onMouseDown}
                    onMouseUp={onMouseUp}
                    onMouseLeave={() => { dragStartX.current = null; }}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Slide track */}
                    <div
                        className="flex transition-transform duration-300 ease-in-out"
                        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                    >
                        {images.map((img, i) => (
                            <div
                                key={i}
                                className="w-full flex-shrink-0 bg-gray-50"
                            >
                                {/* Desktop, unchanged */}
                                <div className="hidden md:flex h-[110px] sm:h-[130px] md:h-[150px] items-center justify-center">
                                    <MediaComponent
                                        src={img}
                                        alt={`Slide ${i + 1}`}
                                        draggable={false}
                                        className="w-full h-full object-contain pointer-events-none"
                                    />
                                </div>
                                {/* Mobile, separate image, full width cover */}
                                <div className="flex md:hidden h-[100px] items-center justify-center">
                                    <MediaComponent
                                        src={mobileImages[i] || img}
                                        alt={`Slide ${i + 1}`}
                                        draggable={false}
                                        className="w-full h-full object-cover pointer-events-none"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Dot pagination */}
                    {images.length > 1 && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                            {images.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => goTo(i)}
                                    className={`rounded-full transition-all duration-300 ${
                                        i === currentIndex
                                            ? 'w-5 h-2 bg-primary-950'
                                            : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                    aria-label={`Go to slide ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default SpecialsBannerSection;
